import { alertClasses, alertTitleClasses, type Components, type ComponentsVariants, type Theme } from '@mui/material'
import type { CSSObject } from '@mui/system'

/**
 * A dedicated variant, not an override of MUI's built-in `standard` - `standard` is still used,
 * unchanged, by pre-existing features that render `<Alert>` with no explicit `variant` (e.g.
 * `SystemStatus/License/Details/NoLicense.tsx`). Scoping to a new variant keeps this migration's
 * blast radius to the components that opt into it (`Toast`/`Banner`/`InlineAlert`).
 */
declare module '@mui/material/Alert' {
    interface AlertPropsVariantOverrides {
        notification: true
    }
}

export type AlertRamp = 'error' | 'warning' | 'success' | 'info'

const RAMPS: readonly AlertRamp[] = ['error', 'warning', 'success', 'info']

/** Each severity's styling is defined independently rather than derived from one shared formula. */
const notificationSeverityStyle: Readonly<Record<AlertRamp, (theme: Theme) => CSSObject>> = {
    info: (theme) => ({
        backgroundColor: theme.palette.info[25],
        color: theme.palette.info[600],
        border: `1px solid ${theme.palette.info[200]}`,
        [`& .${alertClasses.icon}`]: {
            color: theme.palette.info[700],
        },
        [`& .${alertTitleClasses.root}`]: {
            color: theme.palette.info[800],
        },
    }),
    error: (theme) => ({
        backgroundColor: theme.palette.error[25],
        color: theme.palette.error[600],
        border: `1px solid ${theme.palette.error[200]}`,
        [`& .${alertClasses.icon}`]: {
            color: theme.palette.error[600],
        },
        [`& .${alertTitleClasses.root}`]: {
            color: theme.palette.error[800],
        },
    }),
    warning: (theme) => ({
        backgroundColor: theme.palette.warning[25],
        color: theme.palette.warning[600],
        border: `1px solid ${theme.palette.warning[200]}`,
        [`& .${alertClasses.icon}`]: {
            color: theme.palette.warning[600],
        },
        [`& .${alertTitleClasses.root}`]: {
            color: theme.palette.warning[800],
        },
    }),
    success: (theme) => ({
        backgroundColor: theme.palette.success[25],
        color: theme.palette.success[600],
        border: `1px solid ${theme.palette.success[200]}`,
        [`& .${alertClasses.icon}`]: {
            color: theme.palette.success[600],
        },
        [`& .${alertTitleClasses.root}`]: {
            color: theme.palette.success[800],
        },
    }),
}

/**
 * Exposed for `Toast.tsx`'s countdown-bar backdrop, which needs the card's own border color.
 * `string | undefined` because `PaletteColor`'s ramp steps are declared optional - callers narrow with `assert()`.
 */
export const notificationBorderColor: Readonly<Record<AlertRamp, (theme: Theme) => string | undefined>> = {
    info: (theme) => theme.palette.info[200],
    error: (theme) => theme.palette.error[200],
    warning: (theme) => theme.palette.warning[200],
    success: (theme) => theme.palette.success[200],
}

/** Close icon color for `Toast`/`InlineAlert` only - `Banner`'s close icon uses its own dangerGhost/primaryGhost pattern instead. */
export const notificationCloseIconColor: Readonly<Record<AlertRamp, (theme: Theme) => string | undefined>> = {
    info: (theme) => theme.palette.info[400],
    error: (theme) => theme.palette.error[400],
    warning: (theme) => theme.palette.warning[400],
    success: (theme) => theme.palette.success[400],
}

const variants: ComponentsVariants<Theme>['MuiAlert'] = RAMPS.map((severity) => ({
    props: { variant: 'notification', severity },
    style: ({ theme }) => notificationSeverityStyle[severity](theme),
}))

export const MuiAlert: Components<Theme>['MuiAlert'] = {
    variants,
}
